import { useId, useLayoutEffect, useRef, useState } from 'react'
import { Markdown } from '@/components/Markdown'
import type { Role } from '@/lib/types'
import { Byline, feedbackSource } from '@/components/PersonName'
import type { ReviewDetail, ReviewEntry } from './types'

export function ReviewFeedback({
  data,
  role,
  open,
  busy,
  base,
  action,
  title = 'Review feedback',
}: {
  data: ReviewDetail
  role: Role
  open: boolean
  busy: boolean
  base: string
  action: (path: string, body?: unknown) => Promise<boolean | undefined>
  title?: string
}) {
  const report = data.agent_review?.report
  const full = !!data.submission.task.scientific
  const authorId = data.round.author_id ?? data.submission.task.sme_id
  const writer = (actor: ReviewEntry['actor']) => <Byline people={data.people} id={actor.user_id} email={actor.email}
    source={feedbackSource(actor.role, role, actor.user_id === authorId)} />
  return (
    <section className="box">
      <h3>{title}</h3>
      {data.decisions?.map(item => <article className="review-feedback" key={item.entry_id}>
        <div className="row">
          {writer(item.actor)}
          <span className="chip">{item.decision === 'merge' ? 'Approved for publication'
            : item.to_stage === 2 ? 'Returned to expert execution' : 'Returned to build'}</span>
        </div>
        <FeedbackText text={item.feedback ?? ''} full={full} />
      </article>)}
      {data.agent_review && (
        <article className="review-feedback">
          <div className="row">
            <span className="byline"><span className="byline-source">Agent review</span></span>
            <span className={`chip ${report ? report.verdict === 'ready' ? 'ok' : 'warn' : 'neutral'}`}>
              {report ? report.verdict === 'ready' ? 'Ready' : 'Needs changes' : data.agent_review.status}
            </span>
          </div>
          {report ? <FeedbackText text={report.feedback} markdown full={full} /> : (
            <p className="muted small">
              {data.agent_review.error || data.agent_review.activity || 'Waiting for an independent review worker.'}
            </p>
          )}
          {role === 'engineer' && open && ['failed', 'cancelled'].includes(data.agent_review.status) && (
            <button disabled={busy} onClick={() => action(`${base}/agent-retry`)}>
              Retry agent review
            </button>
          )}
        </article>
      )}
      {!full && data.recommendations.length === 0 && <p className="muted">No human reviews submitted yet.</p>}
      {data.recommendations.map((item) => (
        <article className="review-feedback" key={item.entry_id}>
          <div className="row">
            {writer(item.actor)}
            <span className={`chip ${item.verdict === 'ready' ? 'ok' : 'warn'}`}>
              {item.verdict === 'ready' ? 'Ready' : 'Needs changes'}
            </span>
          </div>
          <FeedbackText text={item.feedback ?? ''} full={full} />
        </article>
      ))}
      {data.comments.map((item) => (
        <article className="review-feedback" key={item.entry_id}>
          {writer(item.actor)}
          <FeedbackText text={item.text ?? ''} full={full} />
          {item.reference && (
            <span
              className="small muted"
              title={[item.reference.run_id, item.reference.job_id, item.reference.attempt_id]
                .filter(Boolean)
                .join(' / ')}
            >
              {[
                item.reference.criterion_id,
                item.reference.step ? `Rollout step ${item.reference.step}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          )}
        </article>
      ))}
    </section>
  )
}

/** Scientific findings stay visible while the expert checks the evidence. */
function FeedbackText({ text, markdown = false, full = false }: { text: string; markdown?: boolean; full?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const [long, setLong] = useState(false)
  const body = useRef<HTMLDivElement>(null)
  const id = useId()
  useLayoutEffect(() => {
    const element = body.current
    if (!element) return
    const measure = () => setLong(element.scrollHeight > parseFloat(getComputedStyle(element).lineHeight) * 3 + 1)
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    measure()
    return () => observer.disconnect()
  }, [text])
  return <div className="review-feedback-text">
    <div id={id} className={`review-feedback-copy${long && !expanded && !full ? ' collapsed' : ''}`}
      onClick={long && !expanded && !full ? event => {
        // Keep links and text selection usable; the button also supports keyboard access.
        if ((event.target as HTMLElement).closest('a') || window.getSelection()?.toString()) return
        setExpanded(true)
      } : undefined}>
      <div ref={body}>{markdown ? <Markdown source={text} /> : <p className="prewrap">{text}</p>}</div>
    </div>
    {long && !full && <button type="button" className="link review-feedback-toggle" aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(value => !value)}>
      {expanded ? 'Show less' : 'Read more'}
    </button>}
  </div>
}
